import React, { useMemo, useState } from "react";
import { useFormik } from "formik";

import {
  Container,
  Grid,
  Box,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Theme,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import Copyright from "../../../Utils/Custom/Copyright";
import { passwordValidation } from "../../../Utils/Custom/validations";
import { confirmResetPasswordAPI } from "../../../Utils/API/apiAuth";

import ConfirmationPasswordReseted from "./ConfirmationPasswordReseted";

interface Props {
  oobCode: string;
  emailPasswordToReset: string;
}
function ManageResetPassword({ oobCode, emailPasswordToReset }: Props) {
  const theme: Theme = useTheme();

  const [openModalPasswordReseted, setOpenModalPasswordReseted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const classes = useMemo(
    () => ({
      paper: {
        display: "flex",
        // flexDirection: "column",
        alignItems: "center",
      },
      form: {
        width: "100%", // Fix IE 11 issue.
        marginTop: theme.spacing(3),
      },
      submit: {
        marginTop: theme.spacing(2),
      },
    }),
    []
  );

  const confirmNewPass = async (values: any) => {
    setLoading(true);
    await confirmResetPasswordAPI(oobCode, values.password)
      .then((response) => {
        if (!response.data.hasErrors) {
          setOpenModalPasswordReseted(true);
        } else {
          setTimeout(() => {
            window.location.href = `${process.env.REACT_APP_PortalWebsite}/signin/forgotpassword`;
          }, 2000);
        }
      })
      .catch((error) => {
        //manage in the interceptor
      })
      .finally(() => {
        setLoading(false);
      });
    formik.resetForm();
  };

  const validate = (values: { password: string; repeatPassword: string }) => {
    const errors: any = {};
    if (passwordValidation(values.password)) {
      errors.password = passwordValidation(values.password);
    }
    if (values.password !== values.repeatPassword) {
      errors.repeatPassword = "Passwords do not match";
    }
    return errors;
  };
  const formik = useFormik({
    initialValues: {
      password: "",
      repeatPassword: "",
    },
    validate,
    onSubmit: async (values: any) => {
      await confirmNewPass(values);
    },
    onReset: (values: any) => {
      //
    },
  });
  let emptyForm = true;
  if (formik.values.password && formik.values.repeatPassword) {
    emptyForm = false;
  }

  return (
    <Container component="main" maxWidth="xs">
      <div style={classes.paper}>
        {openModalPasswordReseted ? (
          <ConfirmationPasswordReseted />
        ) : (
          <>
            <Card sx={{ backgroundColor: theme.palette.background.paper }}>
              <CardContent>
                <Grid container flexDirection="column" alignItems="center" sx={{ my: 2 }}>
                  <img
                    src={
                      theme.palette.mode === "light"
                        ? "/images/anavio-logo-light.svg"
                        : "/images/anavio-logo.svg"
                    }
                    alt="Anavio logo"
                    style={{ width: "200px" }}
                  />
                  <Typography component="h1" variant="h5" sx={{ mt: 2 }} color="primary">
                    Reset your password
                  </Typography>
                </Grid>
                <Grid container justifyContent="center">
                  <Typography component="h1" variant="body1">
                    {emailPasswordToReset}
                  </Typography>
                  <form
                    style={classes.form}
                    onSubmit={formik.handleSubmit}
                    onReset={formik.handleReset}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          autoFocus
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          id="password"
                          autoComplete="new-password"
                          {...formik.getFieldProps("password")}
                          disabled={formik.isSubmitting || loading}
                          error={formik.touched.password && formik.errors.password ? true : false}
                          helperText={
                            formik.touched.password && formik.errors.password
                              ? (formik.errors.password as any)
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPassword(!showPassword)}
                                  onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                                    event.preventDefault()
                                  }
                                  edge="end"
                                >
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          autoComplete="new-password"
                          label="ConfirmPassword"
                          type="password"
                          id="repeatPassword"
                          {...formik.getFieldProps("repeatPassword")}
                          disabled={formik.isSubmitting || loading}
                          error={
                            formik.touched.repeatPassword && formik.errors.repeatPassword
                              ? true
                              : false
                          }
                          helperText={
                            formik.touched.repeatPassword && formik.errors.repeatPassword
                              ? (formik.errors.repeatPassword as any)
                              : ""
                          }
                        />
                      </Grid>
                    </Grid>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={classes.submit}
                      disabled={formik.isSubmitting || emptyForm || loading}
                    >
                      Continue
                    </Button>
                  </form>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      {loading ? <LinearProgress style={{ marginTop: "5px" }} /> : null}
      <Box mt={2}>
        <Copyright />
      </Box>
    </Container>
  );
}

export default ManageResetPassword;
